import { useState, useCallback, useEffect } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { ClientForm } from './ClientForm'
import { useClient, useCreateClient, useUpdateClient } from '@/hooks/useClients'
import type { ClientFormValues } from './client-form-schema'
import { getClientDisplayName } from '@/types/clients'

interface ClientSheetProps {
  /** Whether the dialog is open */
  open: boolean
  /** Called when open state changes */
  onOpenChange: (open: boolean) => void
  /** Client ID for edit mode, undefined for create mode */
  clientId?: string
}

/**
 * Dialog wrapper for client create/edit form
 * Handles dirty state detection and close confirmation
 */
export function ClientSheet({ open, onOpenChange, clientId }: ClientSheetProps) {
  const isEdit = !!clientId

  // Form dirty state
  const [isDirty, setIsDirty] = useState(false)
  const [showExitConfirm, setShowExitConfirm] = useState(false)
  const [pendingClose, setPendingClose] = useState(false)

  // Reset dirty state when sheet opens
  // This is intentional - we want to reset state when parent opens the sheet
  useEffect(() => {
    if (open) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setIsDirty(false)
    }
  }, [open])

  // Fetch existing client for edit mode
  const { data: client, isLoading: isLoadingClient } = useClient(
    isEdit ? clientId : undefined
  )

  // Mutations
  const createClient = useCreateClient()
  const updateClient = useUpdateClient()

  const isLoading =
    isLoadingClient || createClient.isPending || updateClient.isPending

  // Handle sheet open change with dirty state check
  const handleOpenChange = useCallback(
    (newOpen: boolean) => {
      if (!newOpen && isDirty) {
        setPendingClose(true)
        setShowExitConfirm(true)
        return
      }
      onOpenChange(newOpen)
      if (!newOpen) {
        setIsDirty(false)
      }
    },
    [isDirty, onOpenChange]
  )

  // Handle confirmed close (discard changes)
  const handleConfirmClose = useCallback(() => {
    setShowExitConfirm(false)
    setIsDirty(false)
    if (pendingClose) {
      setPendingClose(false)
      onOpenChange(false)
    }
  }, [pendingClose, onOpenChange])

  // Handle cancel close (continue editing)
  const handleCancelClose = useCallback(() => {
    setShowExitConfirm(false)
    setPendingClose(false)
  }, [])

  // Handle form submission
  const handleSubmit = async (data: ClientFormValues) => {
    try {
      if (isEdit && clientId) {
        await updateClient.mutateAsync({
          id: clientId,
          data: {
            client_type: data.client_type,
            first_name: data.first_name || null,
            last_name: data.last_name || null,
            company_name: data.company_name || null,
            legal_entity_name: data.legal_entity_name || null,
            email_primary: data.email_primary || null,
            phone_primary: data.phone_primary || null,
            phone_secondary: data.phone_secondary || null,
            address_line1: data.address_line1 || null,
            address_line2: data.address_line2 || null,
            city: data.city || null,
            state_province: data.state_province || null,
            postal_code: data.postal_code || null,
            country: data.country || null,
            payment_terms: data.payment_terms,
            tax_exempt: data.tax_exempt,
            tax_id: data.tax_id || null,
            status: data.status ?? 'active',
            notes: data.notes || null,
          },
        })
      } else {
        await createClient.mutateAsync({
          client_type: data.client_type,
          first_name: data.first_name || undefined,
          last_name: data.last_name || undefined,
          company_name: data.company_name || undefined,
          legal_entity_name: data.legal_entity_name || undefined,
          email_primary: data.email_primary || undefined,
          phone_primary: data.phone_primary || undefined,
          phone_secondary: data.phone_secondary || undefined,
          address_line1: data.address_line1 || undefined,
          address_line2: data.address_line2 || undefined,
          city: data.city || undefined,
          state_province: data.state_province || undefined,
          postal_code: data.postal_code || undefined,
          country: data.country || undefined,
          payment_terms: data.payment_terms,
          tax_exempt: data.tax_exempt,
          tax_id: data.tax_id || undefined,
          status: data.status ?? 'active',
          notes: data.notes || undefined,
        })
      }

      // Success - close sheet
      setIsDirty(false)
      onOpenChange(false)
    } catch (error) {
      console.error('Error saving client:', error)
    }
  }

  // Handle cancel button
  const handleCancel = useCallback(() => {
    handleOpenChange(false)
  }, [handleOpenChange])

  // Prevent closing on interact outside when dirty
  const handleInteractOutside = useCallback(
    (e: Event) => {
      if (isDirty) {
        e.preventDefault()
        setShowExitConfirm(true)
      }
    },
    [isDirty]
  )

  // Prevent closing on escape when dirty
  const handleEscapeKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (isDirty) {
        e.preventDefault()
        setShowExitConfirm(true)
      }
    },
    [isDirty]
  )

  // Track form dirty state via form onChange
  const handleFormChange = useCallback(() => {
    if (!isDirty) {
      setIsDirty(true)
    }
  }, [isDirty])

  const dialogTitle = isEdit
    ? `Edit Client: ${client ? getClientDisplayName(client) : '...'}`
    : 'New Client'

  return (
    <>
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent
          className="sm:max-w-2xl max-h-[90vh] overflow-hidden flex flex-col p-0"
          onInteractOutside={handleInteractOutside}
          onEscapeKeyDown={handleEscapeKeyDown}
        >
          <DialogHeader className="shrink-0 px-6 pt-6 pb-2">
            <DialogTitle>{dialogTitle}</DialogTitle>
          </DialogHeader>

          <div className="flex-1 min-h-0 overflow-y-auto px-6 py-4" onChange={handleFormChange}>
            <ClientForm
              key={clientId ?? 'create'}
              client={client}
              variant="dialog"
              onSubmit={handleSubmit}
            />
          </div>

          {/* Fixed footer with action buttons */}
          <div className="shrink-0 border-t bg-background px-6 py-4 flex justify-end gap-2">
            <Button variant="outline" onClick={handleCancel}>
              Cancel
            </Button>
            <Button type="submit" form="client-form" disabled={isLoading}>
              {isLoading ? 'Saving...' : isEdit ? 'Update Client' : 'Create Client'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Exit Confirmation Dialog */}
      <AlertDialog open={showExitConfirm} onOpenChange={setShowExitConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Discard changes?</AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved changes. Are you sure you want to close? Your
              changes will be lost.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleCancelClose}>
              Continue editing
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmClose}>
              Discard changes
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
